/* ============================================================================
 * fd-cart-add-to-cart-handler.js
 * ============================================================================
 *
 * Wraps window.fetch and XMLHttpRequest once for the whole page:
 *
 *   1. After every successful /cart/add it writes the cart's purchase intent
 *      (_is_subscription_purchase / _is_one_time_purchase), skipping the write
 *      when the cart already holds those values. A caller that wrote them itself
 *      sends the X-FD-Cart-Attributes-Synced header. After a write that changes
 *      lines it clears a subscription flag that no line still backs.
 *
 *   2. It tells FDCart about every cart write (add, change, update, clear) so
 *      the store can tell a fresh cart read from one that raced a write.
 *
 *   window.addToCart(payload) stamps FDCartContext line properties and posts
 *   to /cart/add.js through the wrapped fetch.
 * ========================================================================== */
(function() {
  const CART_ADD_PATH = '/cart/add';
  const CART_UPDATE_PATH = '/cart/update';
  const CART_WRITE_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const CART_ATTRIBUTE_LOG_PREFIX = '[cartattribute]';

  // A caller that already wrote the purchase-intent attribute sets this header on
  // its /cart/add request, so the follow-up attribute write (about 1s) is skipped.
  const ATTRIBUTES_SYNCED_HEADER = 'x-fd-cart-attributes-synced';
  // The gift sync marks its own writes, which skip the gift write gate.
  const GIFT_WRITE_HEADER = 'x-fd-gift-write';

  function getShopifyRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root || root === '/') {
      return '';
    }
    return root.replace(/\/$/, '');
  }

  function hasHeader(headers, name) {
    if (!headers) return false;
    const wanted = name.toLowerCase();
    if (headers instanceof Headers) return headers.has(wanted);
    if (Array.isArray(headers)) return headers.some(pair => String(pair && pair[0]).toLowerCase() === wanted);
    return Object.keys(headers).some(key => key.toLowerCase() === wanted);
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.href === 'string') return input.href;
    return '';
  }

  function requestMethod(input, init) {
    return (init && init.method ? init.method : (input && typeof input === 'object' && input.method) || 'GET').toUpperCase();
  }

  function requestPath(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return String(url || '').split('?')[0];
    }
  }

  // 'add' | 'change' | 'update' | 'clear' for a POST to a cart write endpoint, else ''.
  function cartWriteKind(url, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST' || !url) return '';
    const match = requestPath(url).match(CART_WRITE_PATTERN);
    return match ? match[1] : '';
  }

  function noteWriteStart() {
    const store = window.FDCart;
    if (store && typeof store.noteWriteStart === 'function') return store.noteWriteStart();
    return null;
  }

  function noteWriteEnd(data, seq) {
    const store = window.FDCart;
    if (store && typeof store.noteWriteEnd === 'function') store.noteWriteEnd(data || null, seq);
  }

  // The server can drop one of two writes sent side by side: a cart write waits (up to 3s) for a gift write on its way.
  function giftWriteGate() {
    const gwp = window.FDCartGwp;
    return gwp && typeof gwp.whenGiftWriteDone === 'function' ? gwp.whenGiftWriteDone(3000) : null;
  }

  // change/update answer with the whole cart: hand it to the store (attributes included). Resolves to that cart or null.
  function noteWriteResponse(kind, response, seq) {
    if (!response || !response.ok || (kind !== 'change' && kind !== 'update')) {
      noteWriteEnd(null, seq);
      return Promise.resolve(null);
    }
    return response.clone().json().then(data => {
      noteWriteEnd(data, seq);
      return data;
    }, () => {
      noteWriteEnd(null, seq);
      return null;
    });
  }

  // The cart the caller parses from this response carries its version (FDCart.cartVersion).
  function stampCallerCart(response, version) {
    const store = window.FDCart;
    if (!response || typeof version !== 'number' || !store || typeof store.stampCart !== 'function') return;
    const json = response.json;
    response.json = function() {
      return json.call(response).then(data => store.stampCart(data, version));
    };
  }

  function isCartRead(url, method) {
    return method === 'GET' && /\/cart\.js$/.test(requestPath(url));
  }

  // -------- Body parsing ------------------------------------------------------
  //
  // /cart/add accepts FormData, URLSearchParams, query strings and JSON bodies.
  // normalizeBody reduces them to { type, items } so the attribute logic below
  // can read the items.

  function entriesToFieldMap(entries) {
    const map = new Map();
    for (const [key, value] of entries) {
      if (map.has(key)) {
        const current = map.get(key);
        if (Array.isArray(current)) {
          current.push(value);
        } else {
          map.set(key, [current, value]);
        }
      } else {
        map.set(key, value);
      }
    }
    return map;
  }

  function getFirstValue(value) {
    if (Array.isArray(value)) {
      return value[0];
    }
    return value;
  }

  function extractItemsFromFields(fieldMap) {
    const items = [];

    // ---- Single-item shape: top-level `id` ---------------------------------
    if (fieldMap.has('id')) {
      const base = {
        id: getFirstValue(fieldMap.get('id')),
        quantity: parseInt(getFirstValue(fieldMap.get('quantity')) || '1', 10) || 1
      };
      if (fieldMap.has('selling_plan')) {
        base.selling_plan = getFirstValue(fieldMap.get('selling_plan'));
      }
      // Lift `properties[Key]` form-encoded entries into a real object.
      const properties = {};
      for (const [key, value] of fieldMap.entries()) {
        const propMatch = key.match(/^properties\[(.+)\]$/);
        if (propMatch) {
          properties[propMatch[1]] = getFirstValue(value);
        }
      }
      if (Object.keys(properties).length) {
        base.properties = properties;
      }
      items.push(base);
      return items;
    }

    // ---- Batch shape: `items[N][...]` --------------------------------------
    const itemPattern = /^items\[(\d+)\]\[(.+?)\](?:\[(.+?)\])?$/;
    const collected = {};

    for (const [key, value] of fieldMap.entries()) {
      const match = key.match(itemPattern);
      if (!match) continue;
      const index = parseInt(match[1], 10);
      const attribute = match[2];
      const nested = match[3]; // present for `properties[..]`
      if (!collected[index]) {
        collected[index] = { properties: {} };
      }
      const record = collected[index];
      if (attribute === 'properties' && nested) {
        record.properties[nested] = getFirstValue(value);
      } else if (attribute === 'quantity') {
        record.quantity = parseInt(getFirstValue(value) || '1', 10) || 1;
      } else {
        record[attribute] = getFirstValue(value);
      }
    }

    // Numeric sort so we don't accidentally reorder items lexically (10 < 2).
    const orderedIndexes = Object.keys(collected)
      .map(index => parseInt(index, 10))
      .sort((a, b) => a - b);

    for (const index of orderedIndexes) {
      const entry = collected[index];
      if (!entry.quantity) {
        entry.quantity = 1;
      }
      // Drop empty properties so we don't emit `items[N][properties]` noise.
      if (entry.properties && Object.keys(entry.properties).length === 0) {
        delete entry.properties;
      }
      items.push(entry);
    }

    return items;
  }

  function normalizeBody(body) {
    if (!body) {
      return null;
    }

    if (body instanceof FormData) {
      const fields = entriesToFieldMap(body.entries());
      const items = extractItemsFromFields(fields);
      return { type: 'form-data', fields, items };
    }

    if (body instanceof URLSearchParams) {
      const fields = entriesToFieldMap(body.entries());
      const items = extractItemsFromFields(fields);
      return { type: 'query', fields, items };
    }

    if (typeof body === 'string') {
      const trimmed = body.trim();
      // Heuristic: `{` or `[` at the start suggests JSON.
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const json = JSON.parse(trimmed);
          const items = Array.isArray(json)
            ? json
            : Array.isArray(json.items)
              ? json.items
              : [];
          return { type: 'json', json, items };
        } catch (error) {
          // Looked like JSON but wasn't — fall through to query parsing.
        }
      }
      const params = new URLSearchParams(body);
      const fields = entriesToFieldMap(params.entries());
      const items = extractItemsFromFields(fields);
      return { type: 'query', fields, items };
    }

    if (typeof body === 'object') {
      const json = body;
      const items = Array.isArray(json)
        ? json
        : Array.isArray(json.items)
          ? json.items
          : [];
      return { type: 'json', json, items };
    }

    return null;
  }

  const toStringValue = value => (value == null ? '' : String(value));

  function cloneHeaders(headers) {
    if (!headers) return undefined;
    if (headers instanceof Headers) {
      const cloned = new Headers();
      headers.forEach((value, key) => cloned.append(key, value));
      return cloned;
    }
    return { ...headers };
  }

  function updateFieldsWithItems(fieldMap, items) {
    // ---- Single-item path --------------------------------------------------
    if (fieldMap.has('id') && items.length === 1) {
      const item = items[0];
      fieldMap.set('id', toStringValue(item.id));
      if (item.quantity != null) {
        fieldMap.set('quantity', toStringValue(item.quantity));
      }
      if (item.selling_plan != null) {
        fieldMap.set('selling_plan', toStringValue(item.selling_plan));
      }
      if (item.properties) {
        // Drop any pre-existing properties[..] keys so we don't merge stale data.
        for (const key of [...fieldMap.keys()]) {
          if (key.startsWith('properties[')) {
            fieldMap.delete(key);
          }
        }
        Object.entries(item.properties).forEach(([propertyKey, propertyValue]) => {
          fieldMap.set(`properties[${propertyKey}]`, toStringValue(propertyValue));
        });
      }
      return;
    }

    // ---- Batch path: clear all items[*] keys then re-emit ------------------
    const itemPattern = /^items\[(\d+)\]\[(.+?)\](?:\[(.+?)\])?$/;
    for (const key of [...fieldMap.keys()]) {
      if (itemPattern.test(key)) {
        fieldMap.delete(key);
      }
    }

    items.forEach((item, index) => {
      fieldMap.set(`items[${index}][id]`, toStringValue(item.id));
      fieldMap.set(`items[${index}][quantity]`, toStringValue(item.quantity != null ? item.quantity : 1));
      if (item.selling_plan != null) {
        fieldMap.set(`items[${index}][selling_plan]`, toStringValue(item.selling_plan));
      }
      if (item.properties) {
        Object.entries(item.properties).forEach(([propertyKey, propertyValue]) => {
          fieldMap.set(`items[${index}][properties][${propertyKey}]`, toStringValue(propertyValue));
        });
      }
    });
  }

  function updatePayloadItems(payload, newItems) {
    if (payload.type === 'json') {
      if (Array.isArray(payload.json)) {
        payload.json = newItems;
      } else {
        payload.json.items = newItems;
      }
      return;
    }
    updateFieldsWithItems(payload.fields, newItems);
  }

  function rebuildBody(type, payload) {
    if (type === 'json') {
      return JSON.stringify(payload.json);
    }

    const fieldMap = payload.fields;
    if (type === 'form-data') {
      const formData = new FormData();
      for (const [key, value] of fieldMap.entries()) {
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(key, item));
        } else {
          formData.append(key, value);
        }
      }
      return formData;
    }

    // Default: 'query' / URL-encoded body.
    const params = new URLSearchParams();
    for (const [key, value] of fieldMap.entries()) {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(key, item));
      } else {
        params.append(key, value);
      }
    }
    return params.toString();
  }

  function stampAddPayload(payload) {
    if (!payload || !payload.items.length || !window.FDCartContext || typeof window.FDCartContext.stampPayload !== 'function') return false;
    const snapshot = () => payload.items.map(item => JSON.stringify((item && item.properties) || {})).join('\n');
    const before = snapshot();
    window.FDCartContext.stampPayload({ items: payload.items });
    if (snapshot() === before) return false;
    updatePayloadItems(payload, payload.items);
    return true;
  }

  function preorderTextWait(payload) {
    const context = window.FDCartContext;
    if (!payload || !payload.items.length || !context || typeof context.whenPreorderTextKnown !== 'function') return null;
    return context.whenPreorderTextKnown(payload.items);
  }

  function withPayloadBody(init, payload) {
    const newInit = { ...init };
    newInit.headers = cloneHeaders(init.headers);
    newInit.body = rebuildBody(payload.type, payload);
    if (payload.type === 'json') {
      if (newInit.headers instanceof Headers) {
        if (!newInit.headers.has('Content-Type')) {
          newInit.headers.set('Content-Type', 'application/json');
        }
      } else {
        newInit.headers = { ...(newInit.headers || {}), 'Content-Type': 'application/json' };
      }
    } else if (payload.type === 'query') {
      if (newInit.headers instanceof Headers) {
        if (!newInit.headers.has('Content-Type')) {
          newInit.headers.set('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
        }
      } else {
        newInit.headers = {
          ...(newInit.headers || {}),
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        };
      }
    }
    return newInit;
  }

  // -------- Cart attributes ---------------------------------------------------

  // Free gifts (bundle, welcome kit, box picker, GWP sync) say nothing about the purchase.
  const GIFT_FLAGS = ['_bundle_gift', '_free_gift', '_quantity_welcome_gift', '_welcome_kit_gift', '_box_picker_gift'];
  function isGiftItem(item) {
    const props = (item && item.properties) || {};
    return props._upsell_module === 'checkout_gwp' ||
           GIFT_FLAGS.some(flag => props[flag] === true || String(props[flag]) === 'true');
  }

  function getCartAttributeUpdateForSubscription(payload) {
    if (!payload || !payload.items || payload.items.length === 0) {
      return null;
    }

    // A gift-only add must not relabel a subscription cart as one-time.
    if (payload.items.every(isGiftItem)) {
      return null;
    }

    // Any item flagged as a subscription flips the whole cart's intent.
    const hasSubscription = payload.items.some(item => {
      if (!item) return false;
      // selling_plan is the canonical indicator; the property is a fallback
      // some custom buy buttons emit before Shopify selling plans are picked.
      return item.selling_plan != null ||
             (item.properties && item.properties._is_subscription_purchase === 'true');
    });

    const attributeKey = hasSubscription ? '_is_subscription_purchase' : '_is_one_time_purchase';
    const oppositeKey = hasSubscription ? '_is_one_time_purchase' : '_is_subscription_purchase';

    // Setting the opposite key to '' clears it (Shopify treats empty as unset).
    const update = {
      [attributeKey]: 'true',
      [oppositeKey]: ''
    };
    return update;
  }

  // True when the cart already holds every value of the update ('' means unset).
  function attributesAlreadyMatch(attributeUpdate) {
    const store = window.FDCart;
    const known = store && typeof store.getKnownAttributes === 'function' ? store.getKnownAttributes() : null;
    if (!known) return false;
    return Object.keys(attributeUpdate).every(key => {
      const wanted = attributeUpdate[key] == null ? '' : String(attributeUpdate[key]);
      const current = known[key] == null ? '' : String(known[key]);
      return wanted === current;
    });
  }

  function updateCartAttributes(attributeUpdate, originalFetch) {
    if (!attributeUpdate) {
      return Promise.resolve();
    }

    const params = new URLSearchParams();
    let hasParams = false;
    Object.entries(attributeUpdate).forEach(([key, value]) => {
      if (value == null) return;
      params.append(`attributes[${key}]`, String(value));
      hasParams = true;
    });

    if (!hasParams) {
      return Promise.resolve();
    }

    const updateUrl = `${getShopifyRoot()}${CART_UPDATE_PATH}.js`;
    const seq = noteWriteStart();
    const send = () => originalFetch(updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
      body: params.toString()
    });
    const gate = giftWriteGate();
    return (gate ? gate.then(send) : send()).then(response => {
      noteWriteResponse('update', response, seq);
      if (!response.ok) {
        console.warn(`${CART_ATTRIBUTE_LOG_PREFIX} update failed`, response.status);
      }
      return response;
    }, error => {
      noteWriteEnd(null, seq);
      throw error;
    });
  }

  function syncAttributesAfterAdd(attributeUpdate, originalFetch) {
    if (!attributeUpdate || attributesAlreadyMatch(attributeUpdate)) return;
    updateCartAttributes(attributeUpdate, originalFetch).catch(() => {});
  }

  // A change, or an update that sets line quantities. Attribute-only updates leave the flags alone.
  function writeTouchesLines(kind, body) {
    if (kind === 'change') return true;
    if (kind !== 'update' || !body) return false;
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (trimmed.startsWith('{')) {
        try {
          return Boolean(JSON.parse(trimmed).updates);
        } catch (error) {
          return false;
        }
      }
      body = new URLSearchParams(trimmed);
    }
    if (!(body instanceof FormData) && !(body instanceof URLSearchParams)) return false;
    for (const key of body.keys()) {
      if (key.startsWith('updates')) return true;
    }
    return false;
  }

  // The drawer form's flags for a cart that says subscription while none of its paid lines is one.
  function staleSubscriptionFlagUpdate(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.attributes) return null;
    if (String(cart.attributes._is_subscription_purchase) !== 'true') return null;
    const paid = cart.items.filter(item => item && !isGiftItem(item));
    if (!paid.length) return null;
    const hasSubscription = paid.some(item => item.selling_plan_allocation != null ||
      String((item.properties || {})._is_subscription_purchase) === 'true');
    return hasSubscription ? null : { _is_one_time_purchase: 'true', _is_subscription_purchase: 'false' };
  }

  // A removed or switched subscription line leaves the add's flag behind: clear it once writes settle.
  function clearStaleSubscriptionFlag(written, seq, originalFetch) {
    const store = window.FDCart;
    const settled = store && typeof store.whenWritesIdle === 'function' ? store.whenWritesIdle(3000) : Promise.resolve();
    settled.then(() => {
      const newer = store && typeof store.getWriteState === 'function' && store.getWriteState().started !== seq;
      const cart = newer ? (typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null) : written;
      const update = staleSubscriptionFlagUpdate(cart);
      return update ? updateCartAttributes(update, originalFetch) : null;
    }).catch(() => {});
  }

  // -------- fetch shim -------------------------------------------------------

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function(input, init = {}) {
    const url = requestUrl(input);
    const method = requestMethod(input, init);
    const kind = cartWriteKind(url, method);
    if (!kind) {
      if (!isCartRead(url, method) || !window.FDCart || typeof window.FDCart.readVersion !== 'function') {
        return originalFetch(input, init);
      }
      const version = window.FDCart.readVersion();
      const read = await originalFetch(input, init);
      stampCallerCart(read, version);
      return read;
    }

    const payload = kind === 'add' ? normalizeBody(init && init.body) : null;
    if (payload && stampAddPayload(payload)) init = withPayloadBody(init, payload);
    const attributeUpdate = payload ? getCartAttributeUpdateForSubscription(payload) : null;

    const giftWrite = hasHeader(init && init.headers, GIFT_WRITE_HEADER) ||
      (typeof Request === 'function' && input instanceof Request && hasHeader(input.headers, GIFT_WRITE_HEADER));
    const seq = noteWriteStart();
    let response = null;
    try {
      const preorder = giftWrite ? null : preorderTextWait(payload);
      const gate = giftWrite ? null : giftWriteGate();
      if (gate) await gate;
      if (preorder) {
        await preorder;
        if (stampAddPayload(payload)) init = withPayloadBody(init, payload);
        const lateGate = giftWriteGate();
        if (lateGate) await lateGate;
      }
      response = await originalFetch(input, init);
    } catch (error) {
      noteWriteEnd(null, seq);
      throw error;
    }
    const written = noteWriteResponse(kind, response, seq);
    if (kind !== 'add') stampCallerCart(response, seq);
    if (response.ok && writeTouchesLines(kind, init && init.body)) {
      written.then(cart => clearStaleSubscriptionFlag(cart, seq, originalFetch));
    }

    if (response.ok && attributeUpdate && !hasHeader(init && init.headers, ATTRIBUTES_SYNCED_HEADER)) {
      syncAttributesAfterAdd(attributeUpdate, originalFetch);
    }

    return response;
  };

  // -------- XMLHttpRequest shim ---------------------------------------------

  const originalXHROpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype && window.XMLHttpRequest.prototype.open;
  const originalXHRSend = window.XMLHttpRequest && window.XMLHttpRequest.prototype && window.XMLHttpRequest.prototype.send;
  const originalXHRAbort = window.XMLHttpRequest && window.XMLHttpRequest.prototype && window.XMLHttpRequest.prototype.abort;

  if (originalXHROpen && originalXHRSend) {
    window.XMLHttpRequest.prototype.open = function(method, url, async) {
      this.__fdCartMethod = method;
      this.__fdCartUrl = url;
      this.__fdCartAsync = arguments.length < 3 || Boolean(async);
      return originalXHROpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function(body) {
      const kind = cartWriteKind(this.__fdCartUrl || '', this.__fdCartMethod || 'GET');
      if (!kind) {
        return originalXHRSend.apply(this, arguments);
      }

      const xhr = this;
      const payload = kind === 'add' ? normalizeBody(body) : null;
      let sendArgs = payload && stampAddPayload(payload) ? [rebuildBody(payload.type, payload)] : arguments;
      const attributeUpdate = payload ? getCartAttributeUpdateForSubscription(payload) : null;
      const seq = noteWriteStart();
      const onLoadEnd = () => {
        const ok = xhr.status >= 200 && xhr.status < 300;
        let data = null;
        if (ok && (kind === 'change' || kind === 'update')) {
          try {
            data = JSON.parse(xhr.responseText);
          } catch (error) {
            data = null;
          }
        }
        noteWriteEnd(data, seq);
        if (ok && attributeUpdate) syncAttributesAfterAdd(attributeUpdate, originalFetch);
        if (ok && data && writeTouchesLines(kind, body)) clearStaleSubscriptionFlag(data, seq, originalFetch);
      };
      xhr.addEventListener('loadend', onLoadEnd);
      const preorder = xhr.__fdCartAsync ? preorderTextWait(payload) : null;
      const giftGate = xhr.__fdCartAsync ? giftWriteGate() : null;
      const gate = preorder ? Promise.all([giftGate, preorder]).then(() => {
        if (stampAddPayload(payload)) sendArgs = [rebuildBody(payload.type, payload)];
        return giftWriteGate();
      }).catch(() => {}) : giftGate;
      if (!gate) return originalXHRSend.apply(this, sendArgs);
      // abort() while the gate holds the send cancels it.
      const held = () => {
        xhr.removeEventListener('loadend', onLoadEnd);
        noteWriteEnd(null, seq);
      };
      xhr.__fdCartHeld = held;
      gate.then(() => {
        if (xhr.__fdCartHeld !== held) return;
        xhr.__fdCartHeld = null;
        try {
          originalXHRSend.apply(xhr, sendArgs);
        } catch (error) {
          noteWriteEnd(null, seq);
        }
      });
    };

    window.XMLHttpRequest.prototype.abort = function() {
      const held = this.__fdCartHeld;
      if (held) {
        this.__fdCartHeld = null;
        held();
      }
      return originalXHRAbort.apply(this, arguments);
    };
  }

  // -------- Public helper: window.addToCart ---------------------------------

  window.addToCart = async function(payload, init = {}) {
    let body = null;
    const requestInit = { ...init };

    // Stamp page path + LP/PDP cart context on line items (FDCartContext module).
    if (typeof window.FDCartContext !== 'undefined') {
      window.FDCartContext.stampPayload(payload);
    } else {
      const stampPagePath = (item) => {
        if (!item || typeof item !== 'object') return;
        const props = (item.properties && typeof item.properties === 'object') ? item.properties : {};
        if (props._page_path == null || props._page_path === '') {
          props._page_path = window.location.pathname;
        }
        item.properties = props;
      };
      if (Array.isArray(payload)) {
        payload.forEach(stampPagePath);
      } else if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        payload.items.forEach(stampPagePath);
      }
    }

    if (payload instanceof HTMLFormElement) {
      body = new FormData(payload);
    } else if (payload instanceof FormData) {
      body = payload;
    } else if (payload instanceof URLSearchParams) {
      body = payload;
      if (!requestInit.headers) {
        requestInit.headers = { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' };
      }
    } else if (typeof payload === 'string') {
      body = payload;
    } else if (Array.isArray(payload)) {
      // Bare array → wrap in `{ items: [...] }` since /cart/add.js JSON
      // expects the items key at the top level.
      body = JSON.stringify({ items: payload });
      requestInit.headers = {
        ...(requestInit.headers || {}),
        'Content-Type': 'application/json'
      };
    } else if (payload && typeof payload === 'object') {
      if (payload.items || Array.isArray(payload)) {
        // Already an `{ items: [...] }` shape — send as JSON.
        body = JSON.stringify(payload);
        requestInit.headers = {
          ...(requestInit.headers || {}),
          'Content-Type': 'application/json'
        };
      } else {
        // Single-item shorthand: `{ id, quantity, properties: {...} }`.
        // Serialise to FormData so Shopify's form parser handles it.
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(item => formData.append(key, item));
          } else if (value != null) {
            formData.append(key, value);
          }
        });
        body = formData;
      }
    } else {
      throw new Error('Unsupported payload for addToCart');
    }

    requestInit.method = requestInit.method || 'POST';
    requestInit.body = body;
    return window.fetch('/cart/add.js', requestInit);
  };
})();
